import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../store/themeStore';
import SEO from '../SEO';

// Sections
import Hero from './sections/Hero';
import ServiceGrid from './sections/ServiceGrid';
import Contact from './sections/Contact';
import Footer from './sections/Footer';

// Styles
import {
  ServicesPageContainer,
  Header,
  HeaderContainer,
  HeaderLogo,
  HeaderNav,
  NavLink,
  HeaderRight,
  HeaderButton,
  MenuToggle,
  MobileMenuOverlay,
  MobileNavLink,
  ContentContainer,
} from './styles';

interface ServicesProps {
  onBack: () => void;
  onSwitchToTerminal: () => void;
  onSwitchToClassic: () => void;
  onSwitchToHome: () => void;
}

const Services: React.FC<ServicesProps> = ({ onBack, onSwitchToTerminal, onSwitchToClassic, onSwitchToHome }) => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Track scroll for header shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [menuOpen]);

  const navItems = [
    { id: 'services', label: t('services.title'), href: '#services' },
    { id: 'contact', label: t('services.contact.title'), href: '#contact' },
  ];

  const handleNavClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    if (menuOpen) setMenuOpen(false);
  };

  return (
    <ServicesPageContainer $themeName={theme}>
      <SEO
        title={t('seo.services.title')}
        description={t('seo.services.description')}
        canonicalUrl="https://anilorhan.dev/services"
        schemaType="Service"
      />

      {/* Sticky Header */}
      <Header $themeName={theme} $scrolled={scrolled}>
        <HeaderContainer>
          <HeaderLogo $themeName={theme}>Anıl ORHAN</HeaderLogo>

          <HeaderNav>
            {navItems.map(item => (
              <NavLink
                key={item.id}
                $themeName={theme}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.id)}
              >
                {item.label}
              </NavLink>
            ))}
          </HeaderNav>

          <HeaderRight>
            <HeaderButton $themeName={theme} onClick={onBack}>
              ← {t('ClassicPage.nav.about') === 'About' ? 'Back' : 'Geri'}
            </HeaderButton>
            <MenuToggle $themeName={theme} $isOpen={menuOpen} onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
              <span /><span /><span />
            </MenuToggle>
          </HeaderRight>
        </HeaderContainer>
      </Header>

      {/* Mobile Menu Overlay */}
      <MobileMenuOverlay $themeName={theme} $isOpen={menuOpen}>
        {navItems.map(item => (
          <MobileNavLink
            key={item.id}
            $themeName={theme}
            href={item.href}
            onClick={(e) => handleNavClick(e, item.id)}
          >
            {item.label}
          </MobileNavLink>
        ))}
        <MobileNavLink $themeName={theme} href="#" onClick={(e) => { e.preventDefault(); onSwitchToHome(); setMenuOpen(false); }}>
          {t('ClassicPage.nav.about') === 'About' ? 'Home' : 'Ana Sayfa'}
        </MobileNavLink>
        <MobileNavLink $themeName={theme} href="#" onClick={(e) => { e.preventDefault(); onSwitchToClassic(); setMenuOpen(false); }}>
          {t('ClassicPage.nav.about') === 'About' ? 'Portfolio' : 'Portfolyo'}
        </MobileNavLink>
        <MobileNavLink $themeName={theme} href="#" onClick={(e) => { e.preventDefault(); onSwitchToTerminal(); setMenuOpen(false); }}>
          Terminal
        </MobileNavLink>
      </MobileMenuOverlay>

      {/* Hero */}
      <Hero themeName={theme} />

      {/* Content */}
      <ContentContainer>
        <ServiceGrid themeName={theme} />
        <Contact themeName={theme} />
      </ContentContainer>

      {/* Footer */}
      <Footer
        themeName={theme}
        onSwitchToHome={onSwitchToHome}
        onSwitchToClassic={onSwitchToClassic}
        onSwitchToTerminal={onSwitchToTerminal}
      />
    </ServicesPageContainer>
  );
};

export default Services;
